import { inject } from '@angular/core';
import { CanActivateFn, Router, UrlTree } from '@angular/router';
import { environment } from '../../../../../environment/environment';
import { RbacService } from '../../../../services/rbac/permission.service';
import { IT_ASSET_ACTION_IDS } from '../../../../shared/constants/it-asset-actions';

export const CONFIGURATION_BASE_URL = '/dashboard/assets/asset-configuration';
const ASSETS_BASE_URL = '/dashboard/assets';

export type ConfigurationSectionId = 'categories' | 'tag-numbering' | 'vendors' | 'usage-policies';

export interface ConfigurationSection {
  readonly id: ConfigurationSectionId;
  readonly label: string;
  readonly isNew: boolean;
}

export const CONFIGURATION_SECTIONS: readonly ConfigurationSection[] = [
  { id: 'categories', label: 'Asset categories', isNew: false },
  { id: 'tag-numbering', label: 'Asset tag numbering', isNew: false },
  { id: 'vendors', label: 'Vendor master', isNew: false },
  { id: 'usage-policies', label: 'Usage policies', isNew: true },
];

const SECTION_IDS: ReadonlySet<string> = new Set(CONFIGURATION_SECTIONS.map((section) => section.id));

function isConfigurationSectionId(value: unknown): value is ConfigurationSectionId {
  return typeof value === 'string' && SECTION_IDS.has(value);
}

export function canViewConfigurationSection(rbac: RbacService, id: ConfigurationSectionId): boolean {
  switch (id) {
    case 'categories':
      return rbac.hasItAssetAction(IT_ASSET_ACTION_IDS.VIEW_CATEGORY);
    case 'tag-numbering':
      return (
        rbac.hasItAssetAction(IT_ASSET_ACTION_IDS.VIEW_ASSET_TAG_CONFIG) ||
        rbac.hasItAssetAction(IT_ASSET_ACTION_IDS.EDIT_ASSET_TAG_CONFIG) ||
        rbac.hasItAssetAction(IT_ASSET_ACTION_IDS.FREEZE_ASSET_TAG_CONFIG)
      );
    case 'vendors':
      return (
        rbac.hasItAssetAction(IT_ASSET_ACTION_IDS.GET_VENDOR) ||
        rbac.hasItAssetAction(IT_ASSET_ACTION_IDS.UPDATE_VENDOR)
      );
    case 'usage-policies':
      return (
        environment.disableAssetsRbac ||
        rbac.isAdmin() ||
        rbac.hasItAssetAction(IT_ASSET_ACTION_IDS.VIEW_SOFTWARES)
      );
  }
}

export function visibleConfigurationSections(rbac: RbacService): readonly ConfigurationSection[] {
  return CONFIGURATION_SECTIONS.filter((section) => canViewConfigurationSection(rbac, section.id));
}

export function configurationSectionUrl(id: ConfigurationSectionId): string {
  return `${CONFIGURATION_BASE_URL}/${id}`;
}

function firstAllowedUrl(rbac: RbacService): string {
  const first = visibleConfigurationSections(rbac)[0];
  return first ? configurationSectionUrl(first.id) : ASSETS_BASE_URL;
}

/** `redirectTo` for the bare `asset-configuration` path: opens the first section the user can see. */
export function configurationIndexRedirect(): string {
  return firstAllowedUrl(inject(RbacService));
}

/** Route `data.section` names the section; users without access land on the first section they can see. */
export const configurationSectionGuard: CanActivateFn = (route): boolean | UrlTree => {
  const rbac = inject(RbacService);
  const section: unknown = route.data['section'];
  if (isConfigurationSectionId(section) && canViewConfigurationSection(rbac, section)) {
    return true;
  }
  return inject(Router).parseUrl(firstAllowedUrl(rbac));
};
